import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePageAccess, ASSIGNABLE_PAGES } from "@/hooks/use-page-access";
import { Button } from "@/components/ui/button";
import { AuthLoadingScreen } from "@/components/AuthLoadingScreen";

/**
 * Auth-aware landing route. Decides where to send the user based on the
 * current auth + page-access state. Crucially, it never sends an
 * authenticated user back to /auth — that was the source of the redirect
 * loop (/auth → / → /reservations → /auth).
 */
export default function Index() {
  const { user, loading, signOut } = useAuth();
  const { pages, isSuperAdmin, isLoading: accessLoading } = usePageAccess();

  if (loading || (user && accessLoading)) {
    return <AuthLoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Prefer landing on Performance when the user has access to it.
  if (isSuperAdmin || pages.has("owner-performance")) {
    return <Navigate to="/performance" replace />;
  }

  const first = ASSIGNABLE_PAGES.find((p) => pages.has(p.key));

  if (!first) {
    // Authenticated but no page assigned — show a friendly message instead
    // of bouncing back to /auth (which would create a loop).
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-2xl font-semibold">No pages assigned</h1>
          <p className="text-muted-foreground">
            Your account is signed in but has no pages assigned yet. Please
            contact an administrator.
          </p>
          <Button variant="outline" onClick={() => signOut()} className="mt-2">
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  const routeMap: Record<string, string> = {
    "owner-performance": "/performance",
  };
  return <Navigate to={routeMap[first.key] ?? `/${first.key}`} replace />;
}

